import { useEffect, useMemo, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmModal';
import useAdminAction from '../../hooks/useAdminAction';
import { ADMIN_ACTIONS } from '../../constants/adminActions';
import { businessRef, fetchMerchantById, fetchMerchantInvoices } from '../../services/firestore';

function toDateString(value) {
  if (!value?.toDate) return '-';
  return value.toDate().toLocaleDateString();
}

function toDateTimeString(value) {
  if (!value?.toDate) return '-';
  return value.toDate().toLocaleString();
}

function toInr(value) {
  return `Rs ${Number(value || 0).toLocaleString('en-IN')}`;
}

function initials(value = '') {
  return String(value || 'WB')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'WB';
}

export default function MerchantDetail() {
  const { businessId } = useParams();
  const { performAction, loading: actionLoading, error: actionError } = useAdminAction();
  const [merchant, setMerchant] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trialDays, setTrialDays] = useState(7);
  const [overridePlan, setOverridePlan] = useState('free');
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, type: '' });

  const canExtendTrial = useMemo(() => merchant?.planStatus === 'trial', [merchant]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [merchantDoc, latestInvoices] = await Promise.all([
        fetchMerchantById(businessId),
        fetchMerchantInvoices(businessId, 5),
      ]);
      setMerchant(merchantDoc);
      setInvoices(latestInvoices);
      if (merchantDoc?.plan) setOverridePlan(merchantDoc.plan);
      setInternalNotes(merchantDoc?.internalNotes || '');
    } catch (err) {
      setError(err?.message || 'Failed to load merchant detail.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [businessId]);

  const extendTrial = async () => {
    if (!merchant || Number(trialDays) <= 0) return;
    const currentDate = merchant.trialEndDate?.toDate ? merchant.trialEndDate.toDate() : new Date();
    const nextDate = new Date(currentDate.getTime() + Number(trialDays) * 24 * 60 * 60 * 1000);

    await performAction({
      action: ADMIN_ACTIONS.TRIAL_EXTENDED,
      targetBusinessId: businessId,
      before: { trialEndDate: currentDate.toISOString() },
      after: { trialEndDate: nextDate.toISOString(), daysExtended: Number(trialDays) },
      writeFn: (batch, batchDb) => {
        batch.update(businessRef(batchDb, businessId), {
          trialEndDate: Timestamp.fromDate(nextDate),
          updatedAt: Timestamp.now(),
        });
      },
    });

    setConfirm({ open: false, type: '' });
    await load();
  };

  const overrideMerchantPlan = async () => {
    if (!merchant) return;
    await performAction({
      action: ADMIN_ACTIONS.PLAN_OVERRIDDEN,
      targetBusinessId: businessId,
      before: { plan: merchant.plan || 'free', planOverriddenByAdmin: Boolean(merchant.planOverriddenByAdmin) },
      after: { plan: overridePlan, planOverriddenByAdmin: true },
      writeFn: (batch, batchDb) => {
        batch.update(businessRef(batchDb, businessId), {
          plan: overridePlan,
          planOverriddenByAdmin: true,
          updatedAt: Timestamp.now(),
        });
      },
    });

    setConfirm({ open: false, type: '' });
    await load();
  };

  const applyDiscount = async () => {
    if (!merchant || Number(discountValue) <= 0) return;
    const payload = { type: discountType, value: Number(discountValue) };
    await performAction({
      action: ADMIN_ACTIONS.DISCOUNT_APPLIED,
      targetBusinessId: businessId,
      before: merchant.pendingDiscount ?? null,
      after: payload,
      writeFn: (batch, batchDb) => {
        batch.update(businessRef(batchDb, businessId), {
          pendingDiscount: payload,
          updatedAt: Timestamp.now(),
        });
      },
    });
    setDiscountValue('');
    await load();
  };

  const saveInternalNotes = async () => {
    if (!merchant || (merchant.internalNotes || '') === internalNotes) return;
    setSavingNotes(true);
    try {
      await performAction({
        action: ADMIN_ACTIONS.NOTES_UPDATED,
        targetBusinessId: businessId,
        before: { internalNotes: merchant.internalNotes || '' },
        after: { internalNotes },
        writeFn: (batch, batchDb) => {
          batch.update(businessRef(batchDb, businessId), {
            internalNotes: internalNotes.trim() || null,
            updatedAt: Timestamp.now(),
          });
        },
      });
      await load();
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) return <div className="wb-skeleton wb-skeleton--large" />;
  if (error) return <div className="wb-card wb-error">{error}</div>;
  if (!merchant) return <div className="wb-card">Merchant not found.</div>;

  return (
    <>
      <div className="wb-card wb-card--elevated">
        <div className="wb-card-head">
          <div className="wb-merchant-cell">
            <span className="wb-avatar">{initials(merchant.name || merchant.businessName || businessId)}</span>
            <span>
              <span className="wb-page-kicker">{merchant.plan || 'free'} / {merchant.planStatus || 'unknown'}</span>
              <h3 className="wb-section-title">{merchant.name || merchant.businessName || businessId}</h3>
              <span className="wb-cell-sub">{businessId} - Joined {toDateString(merchant.createdAt)}</span>
            </span>
          </div>
          <span className={`wb-tag wb-tag--${String(merchant.planStatus || 'unknown').toLowerCase()}`}>{merchant.planStatus || '-'}</span>
        </div>

        <div className="wb-kpi-grid">
          <div className="wb-kpi"><span>Invoices</span><strong>{Number(merchant.billCountThisMonth || invoices.length || 0).toLocaleString('en-IN')}</strong><small>Current period</small></div>
          <div className="wb-kpi"><span>MRR</span><strong>{toInr(merchant.mrr || merchant.monthlyRevenue || 0)}</strong><small>{merchant.plan || 'free'} plan</small></div>
          <div className="wb-kpi"><span>Trial end</span><strong>{toDateString(merchant.trialEndDate)}</strong><small>{canExtendTrial ? 'Trial active' : 'Not in trial'}</small></div>
          <div className="wb-kpi"><span>Last seen</span><strong>{toDateString(merchant.updatedAt || merchant.lastSeenAt)}</strong><small>Latest activity</small></div>
        </div>

        {merchant.planOverriddenByAdmin ? (
          <div className="wb-warning-banner">
            This merchant&apos;s plan is admin-managed - Razorpay webhooks will not override it.
          </div>
        ) : null}
      </div>

      <div className="wb-split-grid">
        <div className="wb-card">
          <div className="wb-card-head">
            <div>
              <div className="wb-page-kicker">Last {invoices.length} invoices</div>
              <h3 className="wb-section-title">Activity summary</h3>
            </div>
          </div>
          {invoices.length === 0 ? (
            <p className="wb-muted">No invoices found.</p>
          ) : (
            <table className="wb-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Grand Total</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td><span className="wb-cell-main">{invoice.invoiceNumber || invoice.id}</span><span className="wb-cell-sub">{invoice.paymentStatus || '-'}</span></td>
                    <td>{toInr(invoice.grandTotal)}</td>
                    <td>{toDateTimeString(invoice.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="wb-card">
          <div className="wb-page-kicker">Owner & contact</div>
          <h3 className="wb-section-title">Contact</h3>
          <div className="wb-list-panel">
            <div className="wb-list-row"><span>Phone</span><strong>{merchant.ownerPhone || merchant.phone || '-'}</strong></div>
            <div className="wb-list-row"><span>Email</span><strong>{merchant.email || merchant.ownerEmail || '-'}</strong></div>
            <div className="wb-list-row"><span>GSTIN</span><strong>{merchant.gstNumber || merchant.gstin || merchant.gst || '-'}</strong></div>
            <div className="wb-list-row"><span>City</span><strong>{merchant.city || merchant.state || '-'}</strong></div>
          </div>
        </div>
      </div>

      <div className="wb-card">
        <h3 className="wb-section-title">Actions</h3>
        <div className="wb-actions-grid">
          <div className="wb-action-panel">
            <h4>Extend Trial</h4>
            <input
              type="number"
              min="1"
              value={trialDays}
              onChange={(event) => setTrialDays(event.target.value)}
            />
            <button
              type="button"
              disabled={!canExtendTrial || actionLoading}
              onClick={() => setConfirm({ open: true, type: 'trial' })}
            >
              Extend by days
            </button>
            {!canExtendTrial ? <small className="wb-muted">Available only while merchant is on trial.</small> : null}
          </div>

          <div className="wb-action-panel">
            <h4>Override Plan</h4>
            <select value={overridePlan} onChange={(event) => setOverridePlan(event.target.value)}>
              <option value="free">free</option>
              <option value="starter">starter</option>
              <option value="growth">growth</option>
              <option value="pro">pro</option>
            </select>
            <button type="button" disabled={actionLoading} onClick={() => setConfirm({ open: true, type: 'plan' })}>
              Confirm override
            </button>
          </div>

          <div className="wb-action-panel">
            <h4>Add Subscription Discount</h4>
            <div className="wb-row">
              <select value={discountType} onChange={(event) => setDiscountType(event.target.value)} style={{ maxWidth: 110 }}>
                <option value="percent">%</option>
                <option value="flat">Rs</option>
              </select>
              <input
                type="number"
                min="1"
                value={discountValue}
                onChange={(event) => setDiscountValue(event.target.value)}
                placeholder="Value"
              />
            </div>
            <button type="button" disabled={actionLoading} onClick={applyDiscount}>Save discount</button>
          </div>

          <div className="wb-action-panel">
            <h4>Internal Notes</h4>
            <label className="wb-muted">Internal only - never shown to merchant.</label>
            <textarea
              rows={5}
              value={internalNotes}
              onChange={(event) => setInternalNotes(event.target.value)}
              onBlur={saveInternalNotes}
              placeholder="Add internal notes..."
            />
            {savingNotes ? <small className="wb-muted">Saving...</small> : null}
          </div>
        </div>
        {actionError ? <p className="wb-error-text">{actionError}</p> : null}
      </div>

      <ConfirmModal
        open={confirm.open}
        title={confirm.type === 'trial' ? 'Confirm trial extension' : 'Confirm plan override'}
        description={confirm.type === 'trial'
          ? `Extend trial by ${trialDays} day(s)?`
          : `Override current plan to ${overridePlan}?`}
        onCancel={() => setConfirm({ open: false, type: '' })}
        onConfirm={confirm.type === 'trial' ? extendTrial : overrideMerchantPlan}
      />
    </>
  );
}
